<template>
  <div class="app-rich-text-editor-field">
    <div class="app-rich-text-editor-field__head">
      <span :class="labelClass">{{ resolvedText.messageLabel }}</span>
    </div>

    <div class="app-rich-text-editor-field__wrap">
      <div class="app-rich-text-editor-field__toolbar app-rich-text-editor-field__toolbar--basic">
        <button
          type="button"
          class="app-rich-text-editor-field__tool"
          :title="resolvedText.editorUndoLabel"
          :disabled="disabled || !canEditorUndo"
          @click="runEditorCommand(() => editor.chain().focus().undo().run())"
        >
          <i class="fa fa-undo" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="app-rich-text-editor-field__tool"
          :title="resolvedText.editorRedoLabel"
          :disabled="disabled || !canEditorRedo"
          @click="runEditorCommand(() => editor.chain().focus().redo().run())"
        >
          <i class="fa fa-repeat" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="app-rich-text-editor-field__tool"
          :class="{ active: isMarkActive('bold') }"
          :title="resolvedText.editorBoldLabel"
          :disabled="disabled"
          @click="runEditorCommand(() => editor.chain().focus().toggleBold().run())"
        >
          <i class="fa fa-bold" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="app-rich-text-editor-field__tool"
          :class="{ active: isMarkActive('italic') }"
          :title="resolvedText.editorItalicLabel"
          :disabled="disabled"
          @click="runEditorCommand(() => editor.chain().focus().toggleItalic().run())"
        >
          <i class="fa fa-italic" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="app-rich-text-editor-field__tool"
          :class="{ active: isTextAlignActive('left') }"
          title="Alinear izquierda"
          :disabled="disabled"
          @click="runEditorCommand(() => editor.chain().focus().setTextAlign('left').run())"
        >
          <i class="fa fa-align-left" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="app-rich-text-editor-field__tool"
          :class="{ active: isTextAlignActive('center') }"
          title="Alinear centro"
          :disabled="disabled"
          @click="runEditorCommand(() => editor.chain().focus().setTextAlign('center').run())"
        >
          <i class="fa fa-align-center" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="app-rich-text-editor-field__tool"
          :class="{ active: isTextAlignActive('right') }"
          title="Alinear derecha"
          :disabled="disabled"
          @click="runEditorCommand(() => editor.chain().focus().setTextAlign('right').run())"
        >
          <i class="fa fa-align-right" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="app-rich-text-editor-field__tool"
          :class="{ active: isTextAlignActive('justify') }"
          :title="resolvedText.editorJustifyLabel"
          :disabled="disabled"
          @click="runEditorCommand(() => editor.chain().focus().setTextAlign('justify').run())"
        >
          <i class="fa fa-align-justify" aria-hidden="true"></i>
        </button>

        <div class="app-rich-text-editor-field__toolbar-spacer"></div>

        <button
          type="button"
          class="app-rich-text-editor-field__toggle-btn"
          :disabled="disabled"
          @click="showAdvancedFormatting = !showAdvancedFormatting"
        >
          <i class="fa" :class="showAdvancedFormatting ? 'fa-minus-circle' : 'fa-plus-circle'" aria-hidden="true"></i>
          {{ showAdvancedFormatting ? resolvedText.customizeLessLabel : resolvedText.customizeMoreLabel }}
        </button>
      </div>

      <div v-if="showAdvancedFormatting" class="app-rich-text-editor-field__toolbar app-rich-text-editor-field__toolbar--advanced">
        <div class="app-rich-text-editor-field__select-control">
          <select
            class="app-rich-text-editor-field__select"
            :value="currentHeadingLevel"
            :title="resolvedText.editorHeadingLabel"
            :aria-label="resolvedText.editorHeadingLabel"
            :disabled="disabled"
            @change="setHeadingFromSelect"
          >
            <option value="paragraph">P</option>
            <option value="1">H1</option>
            <option value="2">H2</option>
            <option value="3">H3</option>
          </select>
        </div>

        <div class="app-rich-text-editor-field__select-control">
          <select
            class="app-rich-text-editor-field__select"
            :value="currentFontSize"
            :title="resolvedText.editorFontSizeLabel"
            :aria-label="resolvedText.editorFontSizeLabel"
            :disabled="disabled"
            @change="setFontSizeFromSelect"
          >
            <option value="12px">12</option>
            <option value="14px">14</option>
            <option value="16px">16</option>
            <option value="18px">18</option>
            <option value="20px">20</option>
          </select>
        </div>

        <button
          type="button"
          class="app-rich-text-editor-field__tool"
          :class="{ active: isMarkActive('underline') }"
          :title="resolvedText.editorUnderlineLabel"
          :disabled="disabled"
          @click="runEditorCommand(() => editor.chain().focus().toggleUnderline().run())"
        >
          <i class="fa fa-underline" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="app-rich-text-editor-field__tool"
          :class="{ active: editor && editor.isActive('bulletList') }"
          :title="resolvedText.editorBulletListLabel"
          :disabled="disabled"
          @click="runEditorCommand(() => editor.chain().focus().toggleBulletList().run())"
        >
          <i class="fa fa-list-ul" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="app-rich-text-editor-field__tool"
          :class="{ active: editor && editor.isActive('orderedList') }"
          :title="resolvedText.editorOrderedListLabel"
          :disabled="disabled"
          @click="runEditorCommand(() => editor.chain().focus().toggleOrderedList().run())"
        >
          <i class="fa fa-list-ol" aria-hidden="true"></i>
        </button>

        <div class="app-rich-text-editor-field__color-control" :title="resolvedText.editorColorLabel">
          <span class="app-rich-text-editor-field__color-preview" :style="{ color: currentTextColor }">A</span>
          <i class="fa fa-caret-down" aria-hidden="true"></i>
          <input
            type="color"
            :value="currentTextColor"
            :aria-label="resolvedText.editorColorLabel"
            :disabled="disabled"
            @input="setTextColor"
          />
        </div>

        <div class="app-rich-text-editor-field__color-control" :title="resolvedText.editorBackgroundColorLabel">
          <span
            class="app-rich-text-editor-field__color-preview app-rich-text-editor-field__color-preview--background"
            :style="{ backgroundColor: currentBackgroundColor }"
          ></span>
          <i class="fa fa-caret-down" aria-hidden="true"></i>
          <input
            type="color"
            :value="currentBackgroundColor"
            :aria-label="resolvedText.editorBackgroundColorLabel"
            :disabled="disabled"
            @input="setBackgroundColor"
          />
        </div>

        <button
          type="button"
          class="app-rich-text-editor-field__tool"
          :title="resolvedText.editorClearFormattingLabel"
          :disabled="disabled"
          @click="clearFormatting"
        >
          <i class="fa fa-eraser" aria-hidden="true"></i>
        </button>
      </div>

      <EditorContent :editor="editor" class="app-rich-text-editor-field__editor" />
    </div>
  </div>
</template>

<script>
import { Extension } from '@tiptap/core';
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';

const DEFAULT_TEXT = Object.freeze({
  messageLabel: 'Mensaje',
  customizeMoreLabel: 'Personalizar',
  customizeLessLabel: 'Ocultar opciones',
  editorUndoLabel: 'Atras',
  editorRedoLabel: 'Adelante',
  editorBoldLabel: 'Negritas',
  editorItalicLabel: 'Cursivas',
  editorJustifyLabel: 'Justificar',
  editorUnderlineLabel: 'Subrayado',
  editorBulletListLabel: 'Lista',
  editorOrderedListLabel: 'Lista numerada',
  editorHeadingLabel: 'Encabezado',
  editorFontSizeLabel: 'Tamano',
  editorColorLabel: 'Color de texto',
  editorBackgroundColorLabel: 'Color de fondo',
  editorClearFormattingLabel: 'Limpiar formato',
});

const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {};
              }

              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
          ({ chain }) => {
            return chain().setMark('textStyle', { fontSize }).run();
          },
      unsetFontSize:
        () =>
          ({ chain }) => {
            return chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run();
          },
    };
  },
});

const BackgroundColor = Extension.create({
  name: 'backgroundColor',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          backgroundColor: {
            default: null,
            parseHTML: (element) => element.style.backgroundColor || null,
            renderHTML: (attributes) => {
              if (!attributes.backgroundColor) {
                return {};
              }

              return { style: `background-color: ${attributes.backgroundColor}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setBackgroundColor:
        (backgroundColor) =>
          ({ chain }) => {
            return chain().setMark('textStyle', { backgroundColor }).run();
          },
      unsetBackgroundColor:
        () =>
          ({ chain }) => {
            return chain().setMark('textStyle', { backgroundColor: null }).removeEmptyTextStyle().run();
          },
    };
  },
});

const escapeHtml = (value = '') => {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const normalizeMessageToHtml = (value = '') => {
  const content = String(value || '').trim();
  if (content === '') {
    return '<p></p>';
  }

  if (/<\/?[a-z][\s\S]*>/i.test(content)) {
    return content;
  }

  return content
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br />')}</p>`)
    .join('');
};

const HEX_COLOR_PATTERN = /^#([0-9a-f]{3}){1,2}$/i;

export default {
  name: 'AppRichTextEditorField',
  components: {
    EditorContent,
  },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    text: {
      type: Object,
      default: () => ({}),
    },
    labelClass: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'update:textValue'],
  data() {
    return {
      editor: null,
      showAdvancedFormatting: false,
      currentTextColor: '#1f2937',
      currentBackgroundColor: '#ffffff',
      currentFontSize: '14px',
    };
  },
  computed: {
    resolvedText() {
      return {
        ...DEFAULT_TEXT,
        ...(this.text && typeof this.text === 'object' ? this.text : {}),
      };
    },
    canEditorUndo() {
      return Boolean(this.editor && this.editor.can().undo());
    },
    canEditorRedo() {
      return Boolean(this.editor && this.editor.can().redo());
    },
    currentHeadingLevel() {
      if (!this.editor) {
        return 'paragraph';
      }

      if (this.editor.isActive('heading', { level: 1 })) {
        return '1';
      }

      if (this.editor.isActive('heading', { level: 2 })) {
        return '2';
      }

      if (this.editor.isActive('heading', { level: 3 })) {
        return '3';
      }

      return 'paragraph';
    },
  },
  watch: {
    modelValue(value) {
      this.syncExternalContent(value);
    },
  },
  mounted() {
    this.ensureEditor();
  },
  beforeUnmount() {
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
  },
  methods: {
    ensureEditor() {
      if (this.editor) {
        return;
      }

      this.editor = new Editor({
        extensions: [
          StarterKit,
          TextStyle,
          FontSize,
          BackgroundColor,
          Color,
          Underline,
          TextAlign.configure({
            types: ['heading', 'paragraph'],
          }),
        ],
        content: normalizeMessageToHtml(this.modelValue),
        onCreate: () => {
          this.emitEditorValues();
          this.syncToolbarState();
        },
        onUpdate: () => {
          this.emitEditorValues();
          this.syncToolbarState();
        },
        onSelectionUpdate: () => {
          this.syncToolbarState();
        },
      });
    },
    emitEditorValues() {
      if (!this.editor) {
        return;
      }

      this.$emit('update:modelValue', this.editor.getHTML());
      this.$emit('update:textValue', this.editor.getText());
    },
    syncExternalContent(value) {
      if (!this.editor) {
        return;
      }

      const normalized = normalizeMessageToHtml(value);
      if (normalized === this.editor.getHTML()) {
        return;
      }

      this.editor.commands.setContent(normalized, false);
      this.syncToolbarState();
    },
    syncToolbarState() {
      if (!this.editor) {
        return;
      }

      const textStyle = this.editor.getAttributes('textStyle') || {};
      this.currentTextColor = this.resolveHexColor(textStyle.color, '#1f2937');
      this.currentBackgroundColor = this.resolveHexColor(textStyle.backgroundColor, '#ffffff');
      this.currentFontSize = String(textStyle.fontSize || '14px').trim() || '14px';
    },
    resolveHexColor(value, fallback) {
      const normalized = String(value || '').trim();
      return HEX_COLOR_PATTERN.test(normalized) ? normalized : fallback;
    },
    runEditorCommand(command) {
      if (!this.editor || this.disabled || typeof command !== 'function') {
        return;
      }

      command();
    },
    isMarkActive(markName) {
      return Boolean(this.editor && this.editor.isActive(markName));
    },
    isTextAlignActive(alignment) {
      return Boolean(this.editor && this.editor.isActive({ textAlign: alignment }));
    },
    setHeadingFromSelect(event) {
      const value = String(event?.target?.value || '').trim();
      if (!this.editor || this.disabled) {
        return;
      }

      if (value === 'paragraph') {
        this.editor.chain().focus().setParagraph().run();
        return;
      }

      const level = Number(value || 0);
      if (![1, 2, 3].includes(level)) {
        return;
      }

      this.editor.chain().focus().toggleHeading({ level }).run();
    },
    setFontSizeFromSelect(event) {
      const value = String(event?.target?.value || '').trim();
      if (!this.editor || this.disabled) {
        return;
      }

      if (value === '') {
        this.editor.chain().focus().unsetFontSize().run();
        return;
      }

      this.currentFontSize = value;
      this.editor.chain().focus().setFontSize(value).run();
    },
    setTextColor(event) {
      const value = String(event?.target?.value || '').trim();
      if (!this.editor || this.disabled || !HEX_COLOR_PATTERN.test(value)) {
        return;
      }

      this.currentTextColor = value;
      this.editor.chain().focus().setColor(value).run();
    },
    setBackgroundColor(event) {
      const value = String(event?.target?.value || '').trim();
      if (!this.editor || this.disabled || !HEX_COLOR_PATTERN.test(value)) {
        return;
      }

      this.currentBackgroundColor = value;
      this.editor.chain().focus().setBackgroundColor(value).run();
    },
    clearFormatting() {
      if (!this.editor || this.disabled) {
        return;
      }

      this.editor
        .chain()
        .focus()
        .unsetAllMarks()
        .clearNodes()
        .unsetColor()
        .unsetFontSize()
        .unsetBackgroundColor()
        .run();
    },
  },
};
</script>

<style scoped lang="scss" src="@/assets/scss/domains/app/components/common/_app-rich-text-editor-field.scss"></style>
